import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import {
  useConditionFormState,
  handleSubmit,
  statusOptions,
  ConditionForm,
  ConditionEditMode,
} from "@/lib/ConditionsData";
import useTitanApp from "@/hooks/useTitanApp";

interface ConditionsModalProps {
  trigger: React.ReactNode;
  onSave: (condition: ConditionForm) => void;
  editMode?: ConditionEditMode;
  isDarkMode?: boolean;
}

export function ConditionsModal({
  trigger,
  onSave,
  editMode,
  isDarkMode,
}: ConditionsModalProps) {
  const {
    open,
    setOpen,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useConditionFormState(editMode);

  const status = watch("status");

  const resetFormFields = () => {
    const statusMapForEdit: Record<number, "chronic" | "active" | "resolved"> =
      {
        1: "chronic",
        2: "active",
        3: "resolved",
      };

    if (editMode) {
      reset({
        conditionName: editMode.conditionName || "",
        status:
          typeof editMode.status === "number"
            ? statusMapForEdit[editMode.status] || "chronic"
            : editMode.status,
        diagnosedDate: editMode.diagnosedDate || "",
        notes: editMode.notes || "",
      });
    } else {
      reset({
        conditionName: "",
        status: "chronic",
        diagnosedDate: "",
        notes: "",
      });
    }
  };

  useEffect(() => {
    resetFormFields();
  }, [editMode]);

  const onSubmit = (data: ConditionForm) => {
    handleSubmit(data, onSave, setOpen, reset);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) resetFormFields();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`sm:max-w-[500px] transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {editMode ? "Edit Medical Condition" : "Add Medical Condition"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-6">
          {/* Condition Name */}
          <div className="space-y-2">
            <Label
              htmlFor="conditionName"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Condition Name *
            </Label>
            <Input
              id="conditionName"
              type="text"
              placeholder="e.g., Diabetes, Hypertension, Asthma"
              {...register("conditionName")}
              className="w-full"
            />
            {errors.conditionName && (
              <p className="text-sm text-red-600">
                {errors.conditionName.message}
              </p>
            )}
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Status *
            </Label>
            <Select
              value={status}
              onValueChange={(value: "active" | "resolved" | "chronic") =>
                setValue("status", value)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Diagnosed Date */}
          <div className="space-y-2">
            <Label
              htmlFor="diagnosedDate"
              className="text-sm font-medium text-gray-700"
            >
              Diagnosed Date (optional)
            </Label>
            <Input
              id="diagnosedDate"
              type="date"
              {...register("diagnosedDate")}
              className="w-full"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label
              htmlFor="notes"
              className="text-sm font-medium text-gray-700"
            >
              Additional Notes (optional)
            </Label>
            <Input
              id="notes"
              type="text"
              placeholder="Treatment details, medications, etc."
              {...register("notes")}
              className="w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetFormFields();
                setOpen(false);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-slate-900 hover:bg-slate-800"
            >
              {editMode ? "Update Condition" : "Add Condition"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddConditionButton({
  onSave,
  editMode,
  trigger,
  disabled = false,
}: {
  onSave: (condition: ConditionForm) => void;
  editMode?: ConditionEditMode;
  trigger?: React.ReactNode;
  disabled?: boolean;
}) {
  const { appState } = useTitanApp();
  const { darkMode } = appState || {};
  return (
    <ConditionsModal
      trigger={
        trigger || (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-auto p-0 font-normal text-gray-500 hover:text-gray-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Condition
          </Button>
        )
      }
      onSave={onSave}
      editMode={editMode}
      isDarkMode={darkMode}
    />
  );
}
